import api from '@/api'
import { BaseModalProps } from '@/components/modals/base-modal/base-modal'
import FilePreviewModal from '@/components/modals/FilePreviewModal'
import FormModal from '@/components/modals/FormModal'
import { download } from '@/utils'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Button,
  CircularProgress,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography
} from '@mui/material'
import axios from 'axios'
import { t } from 'i18next'
import React from 'react'
import { toast } from 'react-toastify'

interface FileListModalProps extends BaseModalProps {
  contextType: 'customer' | 'deal' | 'car' | 'report' | 'user'
  contextId: string | number
  onFileDelete?: () => void
}

interface FileItem {
  id: string
  filename: string
  file: string
  uploaded_at: string
  extension: string
}

const FileListModal: React.FC<FileListModalProps> = ({
  isOpen,
  onClose,
  contextType,
  contextId,
  onFileDelete,
}) => {
  const [files, setFiles] = React.useState<FileItem[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [previewFile, setPreviewFile] = React.useState<FileItem | null>(null)

  const fetchFiles = React.useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await api.get<FileItem[]>('files/', {
        [contextType]: contextId,
      })
      setFiles(response.data)
    } catch (error) {
      toast.error(t('error_loading_files'))
    } finally {
      setIsLoading(false)
    }
  }, [contextType, contextId])

  React.useEffect(() => {
    if (isOpen) {
      fetchFiles()
    }
  }, [isOpen, fetchFiles])

  const handleDownload = async (file: FileItem) => {
    try {
      const response = await axios.get(file.file, {
        responseType: 'blob',
      })
      download(response.data, file.filename)
    } catch (error) {
      toast.error(t('error_downloading_file'))
    }
  }

  const handleDelete = async (fileId: string) => {
    if (window.confirm(t('confirm_delete_file'))) {
      try {
        await api.deleteSync(`files/${fileId}/`)
        toast.success(t('file_deleted_successfully'))
        fetchFiles()
        onFileDelete?.()
      } catch (error) {
        toast.error(t('error_deleting_file'))
      }
    }
  }

  const handlePreview = (file: FileItem) => {
    setPreviewFile(file)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        actions={<Button onClick={onClose}>{t('close')}</Button>}
      >
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            {t('files_list')}
          </Typography>

          {isLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '2rem',
              }}
            >
              <CircularProgress />
            </div>
          ) : files.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
              {t('no_files_found')}
            </Typography>
          ) : (
            <List>
              {files.map(file => (
                <ListItem key={file.id} divider>
                  <ListItemText
                    primary={file.filename}
                    secondary={formatDate(file.uploaded_at)}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="preview"
                      onClick={() => handlePreview(file)}
                      sx={{ mr: 1 }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="download"
                      onClick={() => handleDownload(file)}
                      sx={{ mr: 1 }}
                    >
                      <DownloadIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDelete(file.id)}
                      color="error"
                      sx={{ mr: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </FormModal>

      {previewFile && (
        <FilePreviewModal
          isOpen={!!previewFile}
          onClose={() => setPreviewFile(null)}
          {...previewFile}
        />
      )}
    </>
  )
}

export default FileListModal
